import { Text } from '@thanx/uikit/text'
import { buildTranslate } from 'locales'
import moment from 'moment-timezone'
import { FormControl } from 'react-bootstrap-five'
import { timeUtils } from 'utilities/timeUtils'
import { useUserTimeZone } from 'utilities/userTimeZone'

const t = buildTranslate('promo_codes.create_edit.redemption_section.modal')

export const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

export type DayKey = typeof DAYS[number]

// Bitmap of which days are currently selected. The values themselves (hour
// ranges) live in `startHour`/`endHour`; we only flip days on/off here.
export type DaySelection = Record<DayKey, boolean>

export const ALL_DAYS_SELECTED: DaySelection = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: true,
  sunday: true,
}

// Length bounds for the customer-facing time-range description. Mirrors
// Campaign Builder's `redeem_restriction_time_range_description` rules.
export const TIME_DESCRIPTION_MIN_LENGTH = 5
export const TIME_DESCRIPTION_MAX_LENGTH = 47

// 24-hour clock, used to populate the start/end hour selects.
const HOURS = Array.from({ length: 24 }, (_, i) => i)

// Returns the hour range [start..end] inclusive as an array of integers
// suitable for the API's `redeem_restriction_<day>` field.
export function hourRange(startHour: number, endHour: number): number[] {
  if (endHour < startHour) return []
  const out: number[] = []
  for (let h = startHour; h <= endHour; h++) out.push(h)
  return out
}

type DayPillProps = {
  day: DayKey
  active: boolean
  disabled: boolean
  onToggle: (day: DayKey) => void
  label: string
}

const DayPill = ({
  day,
  active,
  disabled,
  onToggle,
  label,
}: DayPillProps): JSX.Element => (
  <button
    type="button"
    className={`promo-day-pill${active ? ' is-active' : ''}`}
    onClick={() => onToggle(day)}
    disabled={disabled}
    aria-pressed={active}
    aria-label={label}
    data-testid={`day-pill-${day}`}
  >
    {label}
  </button>
)

type DayTimePickerProps = {
  // Day selection (which days are enabled)
  days: DaySelection
  onDaysChange: (days: DaySelection) => void
  // Show the day pills section
  showDays: boolean
  // Time-of-day selection
  showTime: boolean
  startHour: number
  endHour: number
  onStartHourChange: (hour: number) => void
  onEndHourChange: (hour: number) => void
  // Customer-facing description (only relevant when showTime)
  description: string
  onDescriptionChange: (value: string) => void
  descriptionError?: string | null
  timeError?: string | null
}

/**
 * Controlled day-of-week + time-of-day picker for the Promo Code wizard's
 * Restrictions modal. Mirrors the visual pattern of Campaign Builder's
 * TimeAndDayPicker but is a plain controlled component (no Formsy) so it
 * fits the wizard's plain-React state model.
 */
const DayTimePicker = ({
  days,
  onDaysChange,
  showDays,
  showTime,
  startHour,
  endHour,
  onStartHourChange,
  onEndHourChange,
  description,
  onDescriptionChange,
  descriptionError,
  timeError,
}: DayTimePickerProps): JSX.Element => {
  const userTimeZone = useUserTimeZone()
  const tzLabel = moment.tz(userTimeZone).format('z')

  const activeDayCount = DAYS.filter(d => days[d]).length

  const handleToggleDay = (day: DayKey) => {
    // Guard: must keep at least one day selected — mirrors the
    // Campaign Builder `isLastDay` behaviour.
    if (days[day] && activeDayCount === 1) return
    onDaysChange({ ...days, [day]: !days[day] })
  }

  return (
    <div>
      {showDays && (
        <div className="mt-m" data-testid="day-pills-container">
          <div className="promo-day-pills">
            {DAYS.map(day => (
              <DayPill
                key={day}
                day={day}
                active={days[day]}
                disabled={days[day] && activeDayCount === 1}
                onToggle={handleToggleDay}
                label={t(`day_short.${day}`)}
              />
            ))}
          </div>
        </div>
      )}

      {showTime && (
        <div className="mt-m" data-testid="time-picker-container">
          <div className="promo-time-row">
            <div>
              <Text.BodyText5 bold className="d-block mb-xs">
                {t('time_start_label')} ({tzLabel})
              </Text.BodyText5>
              <FormControl
                as="select"
                value={String(startHour)}
                onChange={e => onStartHourChange(parseInt(e.target.value, 10))}
                data-testid="time-start-select"
                aria-label={t('time_start_label')}
              >
                {HOURS.map(h => (
                  <option key={h} value={h}>
                    {timeUtils.hourLabel(h, false)}
                  </option>
                ))}
              </FormControl>
            </div>
            <div>
              <Text.BodyText5 bold className="d-block mb-xs">
                {t('time_end_label')} ({tzLabel})
              </Text.BodyText5>
              <FormControl
                as="select"
                value={String(endHour)}
                onChange={e => onEndHourChange(parseInt(e.target.value, 10))}
                data-testid="time-end-select"
                aria-label={t('time_end_label')}
              >
                {HOURS.map(h => (
                  <option key={h} value={h}>
                    {timeUtils.hourLabel(h, true)}
                  </option>
                ))}
              </FormControl>
            </div>
          </div>
          {timeError && (
            <div
              className="promo-inline-error"
              role="alert"
              data-testid="time-error"
            >
              {timeError}
            </div>
          )}

          <div className="mt-m">
            <Text.BodyText5 bold className="d-block mb-xs">
              {t('time_description_label')}
            </Text.BodyText5>
            <FormControl
              type="text"
              value={description}
              onChange={e => onDescriptionChange(e.target.value)}
              placeholder={t('time_description_placeholder')}
              maxLength={TIME_DESCRIPTION_MAX_LENGTH}
              data-testid="time-description-input"
              aria-label={t('time_description_label')}
            />
            {descriptionError && (
              <div
                className="promo-inline-error"
                role="alert"
                data-testid="time-description-error"
              >
                {descriptionError}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Leaf i18n keys returned by `validateTimeRangeDescription`. Callers
// resolve them under the `redemption_section.modal` prefix.
export type TimeDescriptionError =
  | 'time_description_required'
  | 'time_description_min_length'
  | 'time_description_max_length'
  | 'time_description_punctuation'

// Validation helper — mirrors Campaign Builder's
// `redeem_restriction_time_range_description` rules (length 5-47, no
// trailing punctuation, required). Exposed for unit testing.
export function validateTimeRangeDescription(
  value: string
): TimeDescriptionError | null {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return 'time_description_required'
  if (trimmed.length < TIME_DESCRIPTION_MIN_LENGTH)
    return 'time_description_min_length'
  if (trimmed.length > TIME_DESCRIPTION_MAX_LENGTH)
    return 'time_description_max_length'
  // Trailing punctuation includes the common sentence-ending marks. The
  // backend stitches the description into a longer sentence so we want
  // it to read as a noun phrase, not a full sentence.
  if (/[.!?,;:]$/.test(trimmed)) return 'time_description_punctuation'
  return null
}

export default DayTimePicker
